import React, { useState, useMemo } from 'react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { ShoppingCart, Heart, Star } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { useProductPrice } from '../hooks/useMarketZustand';
import { useMarketStore } from '../stores/marketStore';
import { useCartStore } from '../stores/cartStore';
import { useTranslation } from 'react-i18next';

export interface WeightVariant {
  weight: string;
  unit: string;
  price: number;
  pricePerKg: number;
}

export interface Product {
  id: string;
  handle?: string; // Shopify handle for URL routing
  name: string;
  description: string;
  shortDescription?: string; // Short description from metafields
  ingredients?: string; // Ingredients from metafields
  usageTips?: string; // Usage tips from metafields
  allergens?: string; // Allergens from metafields
  nutritionFacts?: {
    calories?: number;
    protein?: number;
    totalFat?: number;
    carbohydrates?: number;
    fiber?: number;
    sodium?: number;
  };
  activeCompounds?: string[];
  healthBenefits?: string[];
  price: number;
  originalPrice?: number;
  image: string;
  rating: number;
  reviewCount: number;
  tags: string[];
  collections?: string[]; // Collection handles this product belongs to
  isNew?: boolean;
  isBestseller?: boolean;
  origin?: string;
  isCertifiedOrganic?: boolean;
  weightVariants?: WeightVariant[];
}

interface ProductCardProps {
  product: Product;
  onToggleFavorite?: (productId: string) => void;
  isFavorite?: boolean;
  onClick?: () => void;
}

export const ProductCard = React.memo(function ProductCard({
  product,
  onToggleFavorite,
  isFavorite = false,
  onClick,
}: ProductCardProps) {
  const { t } = useTranslation('product');
  // 🎯 Ленивая инициализация - вычисляется только один раз при создании
  const [selectedVariant, setSelectedVariant] = useState<WeightVariant | null>(
    () => product.weightVariants?.[0] || null
  );

  // 🎯 АРХИТЕКТУРНОЕ РЕШЕНИЕ: Селективные подписки
  const formatPrice = useMarketStore(state => state.formatPrice); // Функция форматирования цены
  const realPrice = useProductPrice(product.id, product.price); // Только эта цена!

  // 🚀 НОВОЕ: Прямое подключение к CartStore - убираем prop drilling!
  const addToCart = useCartStore(state => state.addToCart);

  // Default weight variants if not provided (assuming grams) - memoized to update when price changes
  const defaultVariants: WeightVariant[] = useMemo(
    () => [
      {
        weight: '100',
        unit: 'g',
        price: realPrice,
        pricePerKg: Math.round((realPrice / 0.1) * 100) / 100, // 100g = 0.1kg
      },
      {
        weight: '250',
        unit: 'g',
        price: realPrice * 2.3,
        pricePerKg: Math.round(((realPrice * 2.3) / 0.25) * 100) / 100, // 250g = 0.25kg
      },
      {
        weight: '500',
        unit: 'g',
        price: realPrice * 4.2,
        pricePerKg: Math.round(((realPrice * 4.2) / 0.5) * 100) / 100, // 500g = 0.5kg
      },
    ],
    [realPrice]
  );

  const variants = product.weightVariants || defaultVariants;

  // 🎯 УМНАЯ ОПТИМИЗАЦИЯ: Сохраняем выбор пользователя, обновляем только цены
  React.useEffect(() => {
    const needsVariantUpdate =
      selectedVariant &&
      variants.length > 0 &&
      selectedVariant.price !==
        variants.find(v => v.weight === selectedVariant.weight)?.price;

    if (needsVariantUpdate) {
      // Находим выбранный пользователем вес среди новых вариантов
      const userChosenWeight = selectedVariant.weight;
      const newVariant =
        variants.find(v => v.weight === userChosenWeight) || variants[0];
      setSelectedVariant(newVariant);
    }
  }, [selectedVariant, variants]);

  // Memoize current price calculation
  const currentPrice = useMemo(
    () => selectedVariant?.price || variants[0]?.price || product.price,
    [selectedVariant, variants, product.price]
  );

  const currentPricePerKg = useMemo(
    () => selectedVariant?.pricePerKg || variants[0]?.pricePerKg,
    [selectedVariant, variants]
  );

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    // 🚀 ПРЯМОЙ ВЫЗОВ CartStore - никаких props!
    addToCart(product, selectedVariant || variants[0]);

    // 🎉 Toast уведомление - теперь ProductCard сам показывает!
    toast.success(t('messages.addedSuccess', { productName: product.name }));
  };

  const handleToggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleFavorite?.(product.id);
  };

  const handleVariantSelect = (variant: WeightVariant, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedVariant(variant);
  };

  const handleCardClick = () => {
    onClick?.();
  };

  // Use short description if available, otherwise truncate regular description
  const displayDescription = product.shortDescription || 
    (product.description.length > 90
      ? product.description.substring(0, 90) + '...'
      : product.description);

  return (
    <Card
      className="group cursor-pointer transition-all duration-500 md:hover:shadow-2xl border-0 shadow-lg overflow-hidden relative"
      onClick={handleCardClick}
      style={{
        background: 'rgba(255, 255, 255, 0.98)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
      }}
    >
      {/* Subtle gradient overlay for 2025 aesthetic */}
      <div
        className="absolute inset-0 opacity-0 md:group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background:
            'linear-gradient(135deg, rgba(245, 158, 11, 0.02) 0%, rgba(217, 119, 6, 0.03) 100%)',
        }}
      />

      <CardContent className="p-0 relative">
        {/* Image Container */}
        <div className="relative overflow-hidden">
          <ImageWithFallback
            src={product.image}
            alt={product.name}
            aspectRatio="1/1"
            className="w-full h-56 object-cover transition-all duration-700 md:group-hover:scale-105"
            lazy
          />

          {/* Minimal badges - only essential ones */}
          <div className="absolute top-4 left-4 flex flex-col gap-2">
            {product.isNew && (
              <Badge
                className="bg-green-500/90 text-white hover:bg-green-600 backdrop-blur-sm"
                style={{ borderRadius: '8px' }}
              >
                {t('badges.new')}
              </Badge>
            )}
            {product.isBestseller && (
              <Badge
                className="bg-amber-500/90 text-white hover:bg-amber-600 backdrop-blur-sm"
                style={{ borderRadius: '8px' }}
              >
                {t('badges.bestseller')}
              </Badge>
            )}
          </div>

          {/* Favorite Button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 bg-white/90 hover:bg-white transition-all duration-300 backdrop-blur-sm opacity-100 scale-100 md:opacity-0 md:scale-95 md:group-hover:opacity-100 md:group-hover:scale-100"
            onClick={handleToggleFavorite}
            style={{ borderRadius: '12px' }}
          >
            <Heart
              className={`h-4 w-4 transition-colors ${
                isFavorite ? 'text-red-500 fill-current' : 'text-gray-600'
              }`}
            />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Title and Origin/Ratings Row */}
          <div>
            <h3 className="font-semibold text-gray-900 md:group-hover:text-amber-900 transition-colors text-lg leading-tight h-14 line-clamp-2">
              {product.name}
            </h3>
            <div className="flex items-center justify-between mt-1">
              {/* Ratings on the left */}
              <div className="flex items-center gap-1">
                <div className="flex items-center">
                  {[...Array(5)].map((_, index) => (
                    <Star
                      key={index}
                      className={`h-3.5 w-3.5 ${
                        index < Math.floor(product.rating)
                          ? 'text-amber-400 fill-current'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-sm text-gray-500">
                  ({product.reviewCount})
                </span>
              </div>

              {/* Origin on the right */}
              {product.origin && (
                <p className="text-sm text-gray-500">
                  {product.origin}
                </p>
              )}
            </div>
          </div>

          {/* Description - 2 lines instead of rating */}
          <div className="text-sm text-gray-600 leading-relaxed">
            <p className="line-clamp-2">{displayDescription}</p>
          </div>

          {/* Weight Variants - Main Feature */}
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900 text-sm">
              {t('actions.chooseSize')}
            </h4>
            <div className="grid grid-cols-3 gap-2">
              {variants.map((variant, index) => (
                <button
                  key={index}
                  onClick={e => handleVariantSelect(variant, e)}
                  className={`p-3 rounded-xl border-2 transition-all duration-300 text-left ${
                    selectedVariant === variant ||
                    (!selectedVariant && index === 0)
                      ? 'border-amber-500 bg-amber-50 shadow-md scale-105'
                      : 'border-gray-200 hover:border-amber-300 hover:bg-amber-25'
                  }`}
                >
                  <div className="text-sm font-semibold text-gray-900">
                    {variant.weight}
                    {variant.unit}
                  </div>
                  <div className="text-sm font-bold text-amber-700 mt-2">
                    {formatPrice(variant.price)}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Price Display - Clean without discount emphasis */}
          <div className="flex items-center justify-between pt-2">
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {formatPrice(currentPrice || 0)}
              </div>
              {currentPricePerKg && (
                <div className="text-sm text-gray-500">
                  {formatPrice(currentPricePerKg)} {t('info.perKg')}
                </div>
              )}
            </div>

            {/* Add to Cart Button */}
            <Button
              className="bg-amber-900 hover:bg-amber-800 text-white font-semibold transition-all duration-300 hover:scale-105 shadow-lg px-6 py-3"
              onClick={handleAddToCart}
              style={{
                background: 'linear-gradient(135deg, #92400e 0%, #78350f 100%)',
                boxShadow: '0 4px 16px rgba(146, 64, 14, 0.25)',
                borderRadius: '12px',
              }}
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              {t('actions.addToCart')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});
